/**
 * Complete tasks on behalf of the operator — admin-authored, audited, not operator submission.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { TrackerLayout } from '@/pages/tracker/TrackerLayout';
import { getJson, http } from '@/lib/http';
import API_BASE from '@/lib/apiBase';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from '@/hooks/use-toast';
import type {
  RecoveryReasonCode,
  RecoveryStoreContextResponse,
  RecoveryStoreTaskDto,
  RecoveryTaskFormatPlanResponse,
  RecoveryVerdict,
  RecoveryProofPublishStatus,
  RecoveryStoreManagerSignatureDto,
} from '@shared/admin-recovery-types';
import { RECOVERY_REASON_CODES } from '@shared/admin-recovery-types';
import {
  buildRecoveryFormatEvidence,
  validateRecoveryFormatDiscrepancies,
} from '@shared/admin-recovery-discrepancy';
import { evaluateRecoveryApproveGate } from '@shared/admin-recovery-approve-gate';
import {
  getRecoveryTaskCompletionSource,
  recoveryCompletionSourceLabel,
  taskNeedsAdminRecoveryAction,
  type RecoveryTaskCompletionSource,
} from '@shared/admin-recovery-task-status';
import { RecoveryFormatQuantitiesTable } from '@/components/admin/RecoveryFormatQuantitiesTable';
import { HeldSignoffStatus } from '@/components/admin/HeldSignoffStatus';
import {
  ArrowLeft,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Camera,
  Upload,
  Clock,
  Trash2,
  Package,
  Plus,
  Sparkles,
  Image as ImageIcon,
  Loader2,
  Smartphone,
  ShieldCheck,
  Filter,
  PenTool,
} from 'lucide-react';

const CAROUSEL_SOURCE_STYLES: Record<
  RecoveryTaskCompletionSource,
  {
    cardBorder: string;
    cardBg: string;
    frameBorder: string;
    sourcePillClass: string;
  }
> = {
  admin_complete: {
    cardBorder: 'border-emerald-300',
    cardBg: 'bg-emerald-50/50',
    frameBorder: 'border-emerald-400/70',
    sourcePillClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  operator_synced: {
    cardBorder: 'border-sky-300',
    cardBg: 'bg-sky-50/50',
    frameBorder: 'border-sky-400/70',
    sourcePillClass: 'bg-sky-100 text-sky-800 border-sky-200',
  },
  needs_admin_action: {
    cardBorder: 'border-amber-300',
    cardBg: 'bg-amber-50/50',
    frameBorder: 'border-amber-400/70',
    sourcePillClass: 'bg-amber-100 text-amber-900 border-amber-200',
  },
  admin_rejected: {
    cardBorder: 'border-rose-300',
    cardBg: 'bg-rose-50/50',
    frameBorder: 'border-rose-400/70',
    sourcePillClass: 'bg-rose-100 text-rose-800 border-rose-200',
  },
  locked: {
    cardBorder: 'border-zinc-300',
    cardBg: 'bg-zinc-50/50',
    frameBorder: 'border-zinc-400/70',
    sourcePillClass: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  },
};

function canOpenRecoverySession(input: {
  reasonCode: RecoveryReasonCode;
  reasonNotes: string;
  externalReference: string;
  auditAcknowledged: boolean;
}): boolean {
  if (!input.auditAcknowledged) return false;
  if (input.reasonCode === 'OTHER' && !input.reasonNotes.trim()) return false;
  if (input.reasonCode === 'EXTERNAL_EVIDENCE' && !input.externalReference.trim()) return false;
  return true;
}

const REASON_LABELS: Record<RecoveryReasonCode, string> = {
  LOST_DEVICE: 'Lost / broken device',
  SYNC_FAILURE: 'Offline sync failure',
  OPERATOR_UNAVAILABLE: 'Operator unavailable',
  RETAILER_DEADLINE: 'Retailer deadline',
  EMERGENCY: 'Emergency intervention',
  EXTERNAL_EVIDENCE: 'WhatsApp / email evidence',
  OTHER: 'Other',
};

export default function AdministrativeStoreRecoveryPage() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const searchParams = new URLSearchParams(window.location.search);
  const cycleId = Number(searchParams.get('cycleId'));
  const storeId = Number(searchParams.get('storeId'));
  const assignedOperatorId = Number(searchParams.get('assignedOperatorId'));

  const [reasonCode, setReasonCode] = useState<RecoveryReasonCode>('SYNC_FAILURE');
  const [reasonNotes, setReasonNotes] = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [auditAcknowledged, setAuditAcknowledged] = useState(false);
  const [closeSummary, setCloseSummary] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [formatDelivered, setFormatDelivered] = useState<Record<number, number>>({});
  const [formatDiscrepancyReasons, setFormatDiscrepancyReasons] = useState<Record<number, string>>({});
  const [resolveNotes, setResolveNotes] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [activeOperatorPhotoIdx, setActiveOperatorPhotoIdx] = useState(0);
  const [showOnlyNeedsAdminAction, setShowOnlyNeedsAdminAction] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [managerSignatureName, setManagerSignatureName] = useState('');
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null);
  const [confirmReplaceSignature, setConfirmReplaceSignature] = useState(false);
  const [showSessionExpiredNotice, setShowSessionExpiredNotice] = useState(false);

  const scopeValid = cycleId > 0 && storeId > 0 && assignedOperatorId > 0;

  const { data: context, isLoading, isFetching, error: contextError, refetch } = useQuery({
    queryKey: ['admin-recovery-store-context', cycleId, storeId, assignedOperatorId],
    queryFn: () =>
      getJson<RecoveryStoreContextResponse & { success: boolean }>(
        `/api/admin/recovery/store-context?cycleId=${cycleId}&storeId=${storeId}&assignedOperatorId=${assignedOperatorId}`,
      ),
    enabled: scopeValid,
  });

  const openSessionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('/api/admin/recovery/sessions', 'POST', {
        cycleId,
        storeId,
        assignedOperatorId,
        reasonCode,
        reasonNotes: reasonNotes.trim() || undefined,
        externalReference: externalReference.trim() || undefined,
        auditAcknowledged,
      });
      return res.json();
    },
    onSuccess: (data: { session?: { id?: number } }) => {
      if (data?.session?.id) {
        setActiveSessionId(data.session.id);
      }
      setShowSessionExpiredNotice(false);
      toast({ title: 'On-behalf session started' });
      refetch();
    },
    onError: (err: Error) => {
      toast({ title: 'Could not open session', description: err.message, variant: 'destructive' });
    },
  });

  const closeSessionMutation = useMutation({
    mutationFn: async (sessionId: number) => {
      const res = await apiRequest(`/api/admin/recovery/sessions/${sessionId}/close`, 'PATCH', {
        closeSummary: closeSummary.trim() || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Session closed' });
      setCloseSummary('');
      refetch();
    },
    onError: (err: Error) => {
      toast({ title: 'Could not close session', description: err.message, variant: 'destructive' });
    },
  });

  const uploadEvidenceMutation = useMutation({
    mutationFn: async ({ sessionId, taskId, file }: { sessionId: number; taskId: number; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('campaignTaskId', String(taskId));
      const res = await http(`${API_BASE}/api/admin/recovery/sessions/${sessionId}/evidence`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || res.statusText);
      }
      return res.json() as Promise<{
        proofPublish?: {
          published?: boolean;
          postingEventId?: number;
          reason?: string;
        };
      }>;
    },
    onSuccess: (data) => {
      if (data?.proofPublish?.published) {
        toast({
          title: 'Photo published to proof',
          description: 'This on-behalf photo is now in gallery and proof of posting.',
        });
      } else if (data?.proofPublish?.reason === 'pending_task_use_approve') {
        toast({
          title: 'Evidence uploaded',
          description: 'Click Approve below to publish this task to proof and review.',
        });
      } else {
        toast({
          title: 'Evidence uploaded',
          description: 'Use Publish to proof if photos do not appear in gallery.',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['admin-recovery-session'] });
      queryClient.invalidateQueries({ queryKey: ['admin-recovery-proof-publish'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    },
  });

  const uploadStoreSignoffMutation = useMutation({
    mutationFn: async ({
      sessionId: sid,
      file,
      managerName,
      replaceExisting,
    }: {
      sessionId: number;
      file: File;
      managerName?: string;
      replaceExisting?: boolean;
    }) => {
      const form = new FormData();
      form.append('file', file);
      if (managerName?.trim()) {
        form.append('managerName', managerName.trim());
      }
      if (replaceExisting) {
        form.append('replaceExisting', 'true');
      }
      const res = await http(`${API_BASE}/api/admin/recovery/sessions/${sid}/store-signoff`, {
        method: 'POST',
        body: form,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const error = new Error(body.error || res.statusText) as Error & {
          code?: string;
          existingSignature?: RecoveryStoreManagerSignatureDto;
        };
        error.code = body.code;
        error.existingSignature = body.existingSignature;
        throw error;
      }
      return body as {
        storeManagerSignature: RecoveryStoreManagerSignatureDto;
        replacedExisting: boolean;
      };
    },
    onSuccess: (data) => {
      toast({
        title: data.replacedExisting ? 'Signature replaced' : 'Store manager signature saved',
        description: 'Visible in Proof of Posting and Tracker as Store Sign-off.',
      });
      setSignatureFile(null);
      setSignaturePreviewUrl(null);
      setManagerSignatureName('');
      setConfirmReplaceSignature(false);
      refetch();
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'SIGNATURE_EXISTS') {
        toast({
          title: 'Signature already on file',
          description: 'Check “Replace existing signature” to upload a new image.',
          variant: 'destructive',
        });
        return;
      }
      if (err.code === 'SESSION_EXPIRED' || /expired|not open/i.test(err.message)) {
        setActiveSessionId(null);
        setShowSessionExpiredNotice(true);
        refetch();
        toast({
          title: 'On-behalf session expired',
          description: 'Start a new session below, then save the signature again.',
          variant: 'destructive',
        });
        return;
      }
      toast({ title: 'Could not save signature', description: err.message, variant: 'destructive' });
    },
  });

  const { data: formatPlan, isLoading: formatPlanLoading } = useQuery({
    queryKey: ['admin-recovery-format-plan', selectedTaskId, assignedOperatorId],
    queryFn: () =>
      getJson<RecoveryTaskFormatPlanResponse>(
        `/api/admin/recovery/tasks/${selectedTaskId}/format-plan?assignedOperatorId=${assignedOperatorId}`,
      ),
    enabled: !!selectedTaskId && assignedOperatorId > 0,
  });

  useEffect(() => {
    if (!formatPlan?.formats?.length) {
      setFormatDelivered({});
      setFormatDiscrepancyReasons({});
      return;
    }
    const initial: Record<number, number> = {};
    for (const row of formatPlan.formats) {
      initial[row.formatId] = row.operatorDelivered ?? row.planned;
    }
    setFormatDelivered(initial);
    setFormatDiscrepancyReasons({});
  }, [formatPlan, selectedTaskId]);

  const deliveredLabel = formatPlan?.taskType === 'REMOVAL' ? 'Removed' : 'Delivered';

  const formatDiscrepancyValidation = useMemo(() => {
    if (!formatPlan?.formats?.length) {
      return { valid: true, hasDiscrepancy: false, missingFormatIds: [] as number[] };
    }
    return validateRecoveryFormatDiscrepancies(
      buildRecoveryFormatEvidence(formatPlan.formats, formatDelivered, formatDiscrepancyReasons),
    );
  }, [formatPlan, formatDelivered, formatDiscrepancyReasons]);

  const approveGate = useMemo(() => {
    if (formatPlanLoading || !formatPlan) {
      return {
        canApprove: false,
        blockReason: null as string | null,
        requiresOverrideNote: false,
      };
    }
    return evaluateRecoveryApproveGate({
      assignmentFound: formatPlan.assignmentFound,
      sessionReasonCode: context?.openSession?.reasonCode ?? '',
      resolveNotes,
    });
  }, [
    formatPlan,
    formatPlanLoading,
    context?.openSession?.reasonCode,
    resolveNotes,
  ]);

  const resolveTaskMutation = useMutation({
    mutationFn: async ({
      sessionId,
      taskId,
      verdict,
    }: {
      sessionId: number;
      taskId: number;
      verdict: RecoveryVerdict;
    }) => {
      const formatQuantities: Record<string, number> = {};
      const formatDiscrepancyPayload: Record<string, string> = {};
      for (const [formatId, qty] of Object.entries(formatDelivered)) {
        formatQuantities[String(formatId)] = qty;
        const reason = formatDiscrepancyReasons[Number(formatId)]?.trim();
        if (reason) {
          formatDiscrepancyPayload[String(formatId)] = reason;
        }
      }
      const postedUnits = Object.values(formatDelivered).reduce((sum, qty) => sum + qty, 0);

      const res = await apiRequest(`/api/admin/recovery/sessions/${sessionId}/actions/resolve`, 'POST', {
        campaignTaskId: taskId,
        verdict,
        postedUnits: postedUnits > 0 ? postedUnits : undefined,
        formatQuantities: Object.keys(formatQuantities).length > 0 ? formatQuantities : undefined,
        formatDiscrepancyReasons:
          Object.keys(formatDiscrepancyPayload).length > 0 ? formatDiscrepancyPayload : undefined,
        notes: resolveNotes.trim() || undefined,
        rejectionNotes: verdict === 'REJECT' ? rejectionNotes.trim() : undefined,
      });
      return res.json();
    },
    onSuccess: (_data, variables) => {
      toast({
        title:
          variables.verdict === 'APPROVE'
            ? 'Task completed by admin'
            : 'Task rejected by admin',
      });
      setResolveNotes('');
      setRejectionNotes('');
      setShowRejectForm(false);
      setFormatDelivered({});
      setFormatDiscrepancyReasons({});
      refetch();

      // Auto-navigate to the next task that still needs admin on-behalf action
      if (context?.tasks?.length) {
        const pool = context.tasks.filter(
          (t) => t.campaignTaskId !== selectedTaskId && taskNeedsAdminRecoveryAction(t),
        );
        if (pool.length > 0) {
          selectTaskForWork(pool[0]);
        }
      }
    },
    onError: (err: Error) => {
      toast({ title: 'Could not complete task', description: err.message, variant: 'destructive' });
    },
  });

  const sessionId = context?.openSession?.id ?? activeSessionId ?? null;
  const sessionIsOpen = context?.openSession?.status === 'open';

  useEffect(() => {
    if (context?.openSession?.id) {
      setActiveSessionId(context.openSession.id);
      setShowSessionExpiredNotice(false);
    } else if (context && !isLoading) {
      setActiveSessionId(null);
    }
  }, [context?.openSession?.id, context, isLoading]);

  const { data: proofPublishStatus } = useQuery({
    queryKey: ['admin-recovery-proof-publish', sessionId, selectedTaskId],
    queryFn: () =>
      getJson<{ status: RecoveryProofPublishStatus }>(
        `/api/admin/recovery/sessions/${sessionId}/tasks/${selectedTaskId}/proof-publish-status`,
      ).then((res) => res.status),
    enabled: !!sessionId && !!selectedTaskId,
  });

  const publishProofMutation = useMutation({
    mutationFn: async ({ sessionId: sid, taskId }: { sessionId: number; taskId: number }) => {
      const res = await apiRequest(`/api/admin/recovery/sessions/${sid}/actions/publish-proof`, 'POST', {
        campaignTaskId: taskId,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: 'Published to proof',
        description: 'On-behalf photos are now visible in gallery and proof of posting.',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-recovery-session'] });
      queryClient.invalidateQueries({ queryKey: ['admin-recovery-proof-publish'] });
      refetch();
    },
    onError: (err: Error) => {
      toast({ title: 'Could not publish to proof', description: err.message, variant: 'destructive' });
    },
  });
  const recoveryEligible = context?.recoveryEligible !== false;
  const recoveryReadOnly = Boolean(context && !recoveryEligible);

  const { data: sessionDetail } = useQuery({
    queryKey: ['admin-recovery-session', sessionId],
    queryFn: () =>
      getJson<{
        evidence: Array<{
          id: number;
          secureUrl: string;
          campaignTaskId: number | null;
        }>;
      }>(`/api/admin/recovery/sessions/${sessionId}`),
    enabled: !!sessionId,
  });

  const evidenceForSelectedTask = useMemo(() => {
    if (!sessionDetail?.evidence?.length || !selectedTaskId) return [];
    return sessionDetail.evidence.filter(
      (ev) => ev.campaignTaskId == null || ev.campaignTaskId === selectedTaskId,
    );
  }, [sessionDetail?.evidence, selectedTaskId]);

  const pendingTasks = useMemo(
    () =>
      (context?.tasks ?? []).filter(
        (t) =>
          !t.hasActiveOverride &&
          t.status !== 'admin_complete' &&
          t.status !== 'admin_rejected' &&
          t.status !== 'locked',
      ),
    [context?.tasks],
  );

  const needsAdminActionTasks = useMemo(
    () => (context?.tasks ?? []).filter(taskNeedsAdminRecoveryAction),
    [context?.tasks],
  );

  const carouselTasks = useMemo(() => {
    if (!context?.tasks?.length) return [];
    if (!showOnlyNeedsAdminAction) return context.tasks;
    return needsAdminActionTasks;
  }, [context?.tasks, showOnlyNeedsAdminAction, needsAdminActionTasks]);

  const selectTaskForWork = (task: RecoveryStoreTaskDto) => {
    setSelectedTaskId(task.campaignTaskId);
    setFormatDelivered({});
    setFormatDiscrepancyReasons({});
    setResolveNotes('');
    setRejectionNotes('');
    setShowRejectForm(false);
  };

  const submitVerdict = (verdict: RecoveryVerdict) => {
    if (!sessionId || !selectedTaskId) return;

    if (!formatDiscrepancyValidation.valid) {
      toast({
        title: 'Discrepancy reason required',
        description: 'Explain each format where delivered quantity differs from planned.',
        variant: 'destructive',
      });
      return;
    }

    if (verdict === 'REJECT' && !rejectionNotes.trim()) {
      toast({
        title: 'Rejection reason required',
        description: 'Describe why this work is not accepted.',
        variant: 'destructive',
      });
      return;
    }

    resolveTaskMutation.mutate({ sessionId, taskId: selectedTaskId, verdict });
  };

  // Carousel navigation calculation
  const activeTaskIndex = useMemo(() => {
    if (!carouselTasks.length || !selectedTaskId) return 0;
    return carouselTasks.findIndex((t) => t.campaignTaskId === selectedTaskId);
  }, [carouselTasks, selectedTaskId]);

  const navigatePrev = useCallback(() => {
    if (!carouselTasks.length || activeTaskIndex === -1) return;
    const prevIdx = activeTaskIndex > 0 ? activeTaskIndex - 1 : carouselTasks.length - 1;
    selectTaskForWork(carouselTasks[prevIdx]);
  }, [carouselTasks, activeTaskIndex]);

  const navigateNext = useCallback(() => {
    if (!carouselTasks.length || activeTaskIndex === -1) return;
    const nextIdx = activeTaskIndex < carouselTasks.length - 1 ? activeTaskIndex + 1 : 0;
    selectTaskForWork(carouselTasks[nextIdx]);
  }, [carouselTasks, activeTaskIndex]);

  // Auto-select first task needing admin action on load
  useEffect(() => {
    if (!sessionId || recoveryReadOnly || !context?.tasks?.length || selectedTaskId) return;
    const firstNeedsAdmin = needsAdminActionTasks[0];
    if (firstNeedsAdmin) {
      selectTaskForWork(firstNeedsAdmin);
      return;
    }
    const firstPending = context.tasks.find(
      (t) =>
        !t.hasActiveOverride &&
        t.status !== 'admin_complete' &&
        t.status !== 'admin_rejected' &&
        t.status !== 'locked',
    );
    if (firstPending) {
      selectTaskForWork(firstPending);
    } else {
      selectTaskForWork(context.tasks[0]);
    }
  }, [sessionId, recoveryReadOnly, context?.tasks, needsAdminActionTasks, selectedTaskId]);

  // When filter hides the selected task, jump to the first visible task
  useEffect(() => {
    if (!showOnlyNeedsAdminAction || !carouselTasks.length || !selectedTaskId) return;
    const stillVisible = carouselTasks.some((t) => t.campaignTaskId === selectedTaskId);
    if (!stillVisible) {
      selectTaskForWork(carouselTasks[0]);
    }
  }, [showOnlyNeedsAdminAction, carouselTasks, selectedTaskId]);

  // Keyboard navigation event listeners
  useEffect(() => {
    if (!sessionId || recoveryReadOnly || !selectedTaskId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      if (
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          (activeEl instanceof HTMLElement && activeEl.isContentEditable))
      ) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigatePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sessionId, recoveryReadOnly, selectedTaskId, navigatePrev, navigateNext]);

  useEffect(() => {
    setActivePhotoIdx(0);
    setActiveOperatorPhotoIdx(0);
  }, [selectedTaskId]);

  useEffect(() => {
    if (!signatureFile) {
      setSignaturePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(signatureFile);
    setSignaturePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [signatureFile]);

  const handleSignatureFileSelect = useCallback((file: File | null) => {
    if (!file) {
      setSignatureFile(null);
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedMime = ['image/jpeg', 'image/png'];
    const allowedExt = ['jpg', 'jpeg', 'png'];
    const mimeOk = !file.type || allowedMime.includes(file.type);
    const extOk = ext ? allowedExt.includes(ext) : false;
    if (!mimeOk && !extOk) {
      toast({
        title: 'Invalid file type',
        description: 'Upload a JPEG or PNG image of the store manager signature.',
        variant: 'destructive',
      });
      return;
    }
    setSignatureFile(file);
  }, []);

  const storeManagerSignature = context?.storeManagerSignature;
  const signatureNeedsReplaceConfirm =
    Boolean(storeManagerSignature?.hasSignature) && !confirmReplaceSignature;
  const canUploadStoreSignature =
    !!sessionId && sessionIsOpen && !recoveryReadOnly && !uploadStoreSignoffMutation.isPending;

  const backParams = new URLSearchParams({ cycleId: String(cycleId) });
  for (const key of ['retailerId', 'year']) {
    const value = searchParams.get(key);
    if (value) backParams.set(key, value);
  }
  const backHref = `/admin/activation-cockpit/progress/stores?${backParams.toString()}`;

  const isUploadingActiveTask =
    uploadEvidenceMutation.isPending &&
    uploadEvidenceMutation.variables?.taskId === selectedTaskId;

  const activeTask = useMemo(() => {
    if (!context?.tasks?.length || !selectedTaskId) return null;
    return context.tasks.find((t) => t.campaignTaskId === selectedTaskId) || null;
  }, [context?.tasks, selectedTaskId]);

  const operatorProgressForSelectedTask = useMemo(() => {
    return formatPlan?.operatorProgress ?? activeTask?.operatorProgress ?? null;
  }, [formatPlan?.operatorProgress, activeTask?.operatorProgress]);

  const operatorSyncedCount = useMemo(() => {
    if (!context?.tasks?.length) return 0;
    return context.tasks.filter(
      (t) => getRecoveryTaskCompletionSource(t) === 'operator_synced',
    ).length;
  }, [context?.tasks]);

  const adminCompletedCount = useMemo(() => {
    if (!context?.tasks?.length) return 0;
    return context.tasks.filter(
      (t) => getRecoveryTaskCompletionSource(t) === 'admin_complete',
    ).length;
  }, [context?.tasks]);

  // Calculate resolution review progress
  const resolvedCount = useMemo(() => {
    if (!context?.tasks?.length) return 0;
    return context.tasks.filter(
      (t) =>
        t.status === 'admin_complete' ||
        t.status === 'admin_rejected' ||
        t.hasActiveOverride
    ).length;
  }, [context?.tasks]);

  const progressPercentage = useMemo(() => {
    if (!context?.tasks?.length) return 0;
    return (resolvedCount / context.tasks.length) * 100;
  }, [context?.tasks, resolvedCount]);

  if (!scopeValid) {
    return (
      <TrackerLayout title="Complete on behalf of operator">
        <Alert variant="destructive">
          <AlertTitle>Missing scope</AlertTitle>
          <AlertDescription>
            Open this page from Cockpit → By Store with cycleId, storeId, and assignedOperatorId.
          </AlertDescription>
        </Alert>
        <Button className="mt-4" variant="outline" onClick={() => setLocation(backHref)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to stores
        </Button>
      </TrackerLayout>
    );
  }

  return (
    <TrackerLayout title={recoveryReadOnly ? "Store signature and delivery status" : "Complete on behalf of operator"}>
      <a className="block text-sm underline p-3" href="/admin/office-emergency-copies">Emergency copies from phones — office use only</a>
      <div className="space-y-4 max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => setLocation(backHref)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to By Store
          </Button>
          {sessionId && (
            <Badge
              variant="outline"
              className={
                sessionIsOpen
                  ? 'border-amber-500 text-amber-800 bg-amber-50 shadow-sm px-3 py-1'
                  : 'border-zinc-400 text-zinc-700 bg-zinc-50 shadow-sm px-3 py-1'
              }
            >
              On-behalf session #{sessionId}
              {sessionIsOpen ? ' active' : ' closed'}
            </Badge>
          )}
        </div>

        {!recoveryReadOnly && <Alert className="border-amber-300 bg-amber-50 shadow-sm">
          <ShieldAlert className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-900 font-semibold">Complete tasks on behalf of the operator</AlertTitle>
          <AlertDescription className="text-amber-800 text-xs">
            Use this when the operator could not finish on site (device lost, sync failure, external evidence).
            You record planned versus actual quantities, approve or reject, and attach evidence — attributed to you
            as admin for{' '}
            <strong>{context?.assignedOperatorName ?? `operator #${assignedOperatorId}`}</strong> at{' '}
            <strong>{context?.storeName ?? `store #${storeId}`}</strong>. For tasks already marked complete on the
            server, uploaded photos are <strong>auto-published to proof</strong>. Otherwise click{' '}
            <strong>Approve</strong> (which also auto-approves for review). Use Evidence on By Store for full review
            controls.
          </AlertDescription>
        </Alert>}

        {(context || contextError) && <HeldSignoffStatus status={contextError ? { state: 'unavailable' } : context?.heldSignoff} />}
        {(context || contextError) && <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>Refresh store context</Button>}

        {recoveryReadOnly && (
          <Alert className="border-border bg-muted/30">
            <AlertTitle className="font-semibold">All tasks complete</AlertTitle>
            <AlertDescription className="text-xs">
              {context?.recoveryIneligibleReason ??
                'All tasks at this store are finished. Completing on behalf of the operator is only for outstanding work.'}
            </AlertDescription>
          </Alert>
        )}

        {isLoading && <p className="text-sm text-muted-foreground animate-pulse">Loading store context…</p>}

        {!sessionIsOpen && context && !recoveryReadOnly && (
          <>
            {showSessionExpiredNotice && (
              <Alert variant="destructive">
                <AlertTitle>Previous session expired</AlertTitle>
                <AlertDescription className="text-xs">
                  Open a new on-behalf session below, then click <strong>Save signature</strong> again (your
                  selected image is still loaded).
                </AlertDescription>
              </Alert>
            )}
          <Card className="shadow-md border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                <span>Start on-behalf session</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="font-semibold text-xs text-muted-foreground uppercase">Reason</Label>
                  <Select value={reasonCode} onValueChange={(v) => setReasonCode(v as RecoveryReasonCode)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECOVERY_REASON_CODES.map((code) => (
                        <SelectItem key={code} value={code}>
                          {REASON_LABELS[code]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {reasonCode === 'EXTERNAL_EVIDENCE' && (
                  <div className="space-y-2">
                    <Label className="font-semibold text-xs text-muted-foreground uppercase">External reference (required)</Label>
                    <Input
                      value={externalReference}
                      onChange={(e) => setExternalReference(e.target.value)}
                      placeholder="WhatsApp thread, email subject, or ticket ID"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-xs text-muted-foreground uppercase">Notes {reasonCode === 'OTHER' ? '(required)' : '(optional)'}</Label>
                <Textarea value={reasonNotes} onChange={(e) => setReasonNotes(e.target.value)} rows={3} placeholder="Provide audit explanation..." />
              </div>
              <div className="flex items-start gap-2.5 bg-muted/30 p-3 rounded-lg border">
                <Checkbox
                  id="audit-ack"
                  checked={auditAcknowledged}
                  onCheckedChange={(v) => setAuditAcknowledged(v === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="audit-ack" className="text-xs leading-snug cursor-pointer font-medium text-foreground select-none">
                  I am completing outstanding work on behalf of the operator. Actions are permanently audited,
                  attributed to me as admin, and are not operator mobile submissions.
                </Label>
              </div>
              <Button
                onClick={() => openSessionMutation.mutate()}
                size="lg"
                className="w-full md:w-auto font-semibold bg-amber-600 hover:bg-amber-500 text-white"
                disabled={
                  !canOpenRecoverySession({
                    reasonCode,
                    reasonNotes,
                    externalReference,
                    auditAcknowledged,
                  }) || openSessionMutation.isPending
                }
              >
                Begin session
              </Button>
            </CardContent>
          </Card>
          </>
        )}

        {sessionId && context && sessionIsOpen && (
          <div className="space-y-6">
            {/* Unified Session Header with Progress Bar */}
            <div className="rounded-xl border bg-muted/40 p-4 shadow-sm space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs font-semibold text-foreground flex items-center gap-2">
                  <span className="text-muted-foreground uppercase">Resolution Progress:</span>
                  <Badge variant="secondary" className="bg-primary/10 text-primary font-bold">
                    {resolvedCount} of {context.tasks.length} tasks resolved
                  </Badge>
                </div>
                <div className="flex gap-2 text-xs font-semibold text-muted-foreground">
                  <span className="text-sky-700 bg-sky-50 px-2 py-0.5 rounded border border-sky-100 flex items-center gap-1">
                    <Smartphone className="h-3.5 w-3.5" />
                    {operatorSyncedCount} operator
                  </span>
                  <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100 flex items-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {adminCompletedCount} admin
                  </span>
                  <span className="text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-100 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {needsAdminActionTasks.length} need action
                  </span>
                  <span className="text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-100 flex items-center gap-1">
                    <XCircle className="h-3.5 w-3.5" />
                    {context.tasks.filter((t) => t.status === 'admin_rejected').length} rejected
                  </span>
                </div>
              </div>
              <div className="flex h-2 rounded-full overflow-hidden bg-muted border">
                <div
                  className="bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            <Card className="border border-green-200 bg-green-50/30 shadow-sm">
              <CardHeader className="py-4">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-green-900">
                  <PenTool className="h-4 w-4" />
                  Store manager signature
                  <Badge variant="outline" className="text-[10px] font-normal border-green-300 text-green-800">
                    Optional
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                <p className="text-xs text-green-900/80">
                  Optional store manager signature — saved to the same store visit record as mobile sign-off and
                  shown in Proof and Tracker the same way. You can upload anytime during this session; it is not
                  required at the end and you do not need to approve tasks first.
                </p>

                {storeManagerSignature?.hasSignature && (
                  <div className="rounded-lg border border-green-300 bg-white p-3 space-y-2">
                    <p className="text-xs font-semibold text-green-800 flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Signature on file
                      {storeManagerSignature.submissionSource === 'admin' && (
                        <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-900">
                          Admin captured
                        </Badge>
                      )}
                    </p>
                    {storeManagerSignature.signedBy && (
                      <p className="text-xs text-muted-foreground">
                        Manager: <strong>{storeManagerSignature.signedBy}</strong>
                      </p>
                    )}
                    {storeManagerSignature.signedAt && (
                      <p className="text-xs text-muted-foreground">
                        Signed: {new Date(storeManagerSignature.signedAt).toLocaleString()}
                      </p>
                    )}
                    {storeManagerSignature.signatureUrl && (
                      <img
                        src={storeManagerSignature.signatureUrl}
                        alt="Current store manager signature"
                        className="max-h-24 rounded border bg-white object-contain"
                      />
                    )}
                  </div>
                )}

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase text-muted-foreground">
                      Manager name (optional)
                    </Label>
                    <Input
                      value={managerSignatureName}
                      onChange={(e) => setManagerSignatureName(e.target.value)}
                      placeholder="Store manager name"
                      disabled={!canUploadStoreSignature}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase text-muted-foreground">
                      Signature image (JPEG/PNG)
                    </Label>
                    <Input
                      type="file"
                      accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                      onChange={(e) => handleSignatureFileSelect(e.target.files?.[0] ?? null)}
                      disabled={!canUploadStoreSignature}
                      className="text-xs"
                    />
                  </div>
                </div>

                {signaturePreviewUrl && signatureFile && !storeManagerSignature?.hasSignature && (
                  <p className="text-xs font-medium text-green-800 bg-green-100/80 border border-green-200 rounded-md px-3 py-2">
                    Image selected — click <strong>Save signature</strong> below. Saves like mobile sign-off;
                    task Approve/Reject below is still separate.
                  </p>
                )}

                {signaturePreviewUrl && (
                  <div className="rounded border bg-white p-2 inline-block">
                    <p className="text-[10px] text-muted-foreground mb-1">Preview</p>
                    <img
                      src={signaturePreviewUrl}
                      alt="Signature preview"
                      className="max-h-24 object-contain"
                    />
                  </div>
                )}

                {storeManagerSignature?.hasSignature && canUploadStoreSignature && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <Checkbox
                      id="replace-signature"
                      checked={confirmReplaceSignature}
                      onCheckedChange={(v) => setConfirmReplaceSignature(v === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="replace-signature" className="text-xs leading-snug cursor-pointer">
                      Replace existing signature — overwrites the current store sign-off image for this cycle.
                    </Label>
                  </div>
                )}

                <Button
                  type="button"
                  variant={signatureFile ? 'default' : 'outline'}
                  className={
                    signatureFile
                      ? 'bg-green-700 hover:bg-green-600 text-white'
                      : 'border-green-600 text-green-800 hover:bg-green-100'
                  }
                  disabled={
                    !signatureFile ||
                    !canUploadStoreSignature ||
                    signatureNeedsReplaceConfirm
                  }
                  onClick={() => {
                    if (!sessionId || !signatureFile) return;
                    uploadStoreSignoffMutation.mutate({
                      sessionId,
                      file: signatureFile,
                      managerName: managerSignatureName,
                      replaceExisting: confirmReplaceSignature,
                    });
                  }}
                >
                  {uploadStoreSignoffMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      {storeManagerSignature?.hasSignature ? 'Replace signature' : 'Save signature'}
                    </>
                  )}
                </Button>
                {signatureNeedsReplaceConfirm && (
                  <p className="text-xs text-amber-800">
                    A signature is already on file — tick Replace existing signature above before saving a new
                    image.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Central Carousel Workspace Container */}
            {selectedTaskId && carouselTasks.some((t) => t.campaignTaskId === selectedTaskId) && (
              <div className="relative group/carousel">
                {/* Float navigation arrow left */}
                <button
                  type="button"
                  title="Previous task (Keyboard: Left Arrow)"
                  onClick={navigatePrev}
                  className="absolute -left-12 top-1/2 -translate-y-1/2 hidden md:flex w-9 h-9 rounded-full bg-card hover:bg-muted text-muted-foreground hover:text-foreground items-center justify-center border shadow-md transition-all hover:scale-105 active:scale-95"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                {/* Float navigation arrow right */}
                <button
                  type="button"
                  title="Next task (Keyboard: Right Arrow)"
                  onClick={navigateNext}
                  className="absolute -right-12 top-1/2 -translate-y-1/2 hidden md:flex w-9 h-9 rounded-full bg-card hover:bg-muted text-muted-foreground hover:text-foreground items-center justify-center border shadow-md transition-all hover:scale-105 active:scale-95"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                  {/* Left Pane: Evidence Box */}
                  <div className="lg:col-span-7 flex flex-col gap-4">
                    <div className="flex-1 flex flex-col bg-card border rounded-xl p-4 shadow-sm space-y-4">
                      {/* Operator-synced evidence */}
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                          <Smartphone className="h-3.5 w-3.5 text-sky-600" />
                          <span>Operator evidence (synced)</span>
                          {operatorProgressForSelectedTask?.submittedAt && (
                            <span className="normal-case font-normal text-[10px] text-muted-foreground ml-auto">
                              {new Date(operatorProgressForSelectedTask.submittedAt).toLocaleString()}
                            </span>
                          )}
                        </div>

                        {operatorProgressForSelectedTask?.photos?.length ? (
                          <div className="space-y-2">
                            <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-900 border flex items-center justify-center min-h-[220px] max-h-[320px]">
                              <img
                                src={operatorProgressForSelectedTask.photos[activeOperatorPhotoIdx]?.secureUrl}
                                alt=""
                                className="max-h-[320px] w-full object-contain"
                              />
                              {operatorProgressForSelectedTask.photos.length > 1 && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setActiveOperatorPhotoIdx((prev) =>
                                        prev > 0
                                          ? prev - 1
                                          : operatorProgressForSelectedTask.photos.length - 1,
                                      )
                                    }
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center shadow transition-colors"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setActiveOperatorPhotoIdx((prev) =>
                                        prev < operatorProgressForSelectedTask.photos.length - 1
                                          ? prev + 1
                                          : 0,
                                      )
                                    }
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center shadow transition-colors"
                                  >
                                    <ChevronRight className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              <div className="absolute top-3 right-3 bg-sky-600/80 backdrop-blur-sm text-white text-[11px] px-2.5 py-1 rounded-full font-medium shadow-sm">
                                Operator photo {activeOperatorPhotoIdx + 1} of{' '}
                                {operatorProgressForSelectedTask.photos.length}
                              </div>
                            </div>
                            {operatorProgressForSelectedTask.installerNote && (
                              <p className="text-xs text-muted-foreground bg-sky-50/60 border border-sky-100 rounded-lg p-2.5">
                                <span className="font-semibold text-sky-900">Installer note: </span>
                                {operatorProgressForSelectedTask.installerNote}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed bg-muted/20 p-4 text-center">
                            <p className="text-xs text-muted-foreground">
                              No operator evidence synced for this task yet.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Admin session evidence */}
                      <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                        <ImageIcon className="h-3.5 w-3.5 text-primary" />
                        <span>Admin evidence (this session)</span>
                      </div>

                      {evidenceForSelectedTask.length === 0 ? (
                        /* Modern Drag and Drop Dropzone */
                        <div
                          onDragOver={(e) => {
                            if (!sessionIsOpen) return;
                            e.preventDefault();
                            setIsDragging(true);
                          }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={(e) => {
                            if (!sessionIsOpen) return;
                            e.preventDefault();
                            setIsDragging(false);
                            const file = e.dataTransfer.files?.[0];
                            if (file && sessionId) {
                              uploadEvidenceMutation.mutate({
                                sessionId,
                                taskId: selectedTaskId,
                                file,
                              });
                            }
                          }}
                          onClick={() => {
                            if (!sessionIsOpen) return;
                            document.getElementById('evidence-upload-input')?.click();
                          }}
                          className={`flex-1 min-h-[200px] border-2 border-dashed rounded-xl flex flex-col items-center justify-center p-6 text-center transition-all relative ${
                            !sessionIsOpen
                              ? 'border-muted-foreground/20 bg-muted/10 cursor-not-allowed opacity-70'
                              : isDragging
                              ? 'border-primary bg-primary/5 scale-[1.01] cursor-pointer'
                              : 'border-muted-foreground/30 bg-muted/5 hover:border-primary/50 hover:bg-primary/[0.02] cursor-pointer'
                          }`}
                        >
                          <input
                            id="evidence-upload-input"
                            type="file"
                            accept="image/jpeg,image/png"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file && sessionId) {
                                uploadEvidenceMutation.mutate({
                                  sessionId,
                                  taskId: selectedTaskId,
                                  file,
                                });
                                e.target.value = '';
                              }
                            }}
                          />

                          {isUploadingActiveTask ? (
                            <div className="flex flex-col items-center gap-3">
                              <Loader2 className="h-10 w-10 text-primary animate-spin" />
                              <p className="font-semibold text-sm text-foreground">Uploading evidence photo...</p>
                              <p className="text-xs text-muted-foreground">Uploading to Supabase storage</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-4 max-w-sm">
                              <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center shadow-inner">
                                <Plus className="h-6 w-6" />
                              </div>
                              <div>
                                <p className="font-semibold text-sm text-foreground">Upload Evidence Photo</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {sessionIsOpen
                                    ? 'Drag and drop a JPEG or PNG photo here, or click to browse your files. Complete tasks auto-publish to proof.'
                                    : 'Session is closed — you can still publish existing photos using Publish to proof above.'}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Beautiful Image Viewer */
                        <div className="relative rounded-xl overflow-hidden shadow-sm bg-slate-900 border flex items-center justify-center min-h-[380px] max-h-[480px]">
                          <img
                            src={evidenceForSelectedTask[activePhotoIdx].secureUrl}
                            alt=""
                            className="max-h-[480px] w-full object-contain"
                          />

                          {/* Photo index count overlay */}
                          <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[11px] px-2.5 py-1 rounded-full font-medium shadow-sm">
                            Photo {activePhotoIdx + 1} of {evidenceForSelectedTask.length}
                          </div>

                          {/* Left / Right overlay indicators for task photo carousel */}
                          {evidenceForSelectedTask.length > 1 && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActivePhotoIdx((prev) =>
                                    prev > 0 ? prev - 1 : evidenceForSelectedTask.length - 1
                                  );
                                }}
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center shadow transition-colors"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActivePhotoIdx((prev) =>
                                    prev < evidenceForSelectedTask.length - 1 ? prev + 1 : 0
                                  );
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center shadow transition-colors"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </>
                          )}

                          {/* Inner Uploading Spinner overlay */}
                          {isUploadingActiveTask && (
                            <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                              <Loader2 className="h-8 w-8 text-primary animate-spin" />
                              <p className="text-xs font-semibold text-white">Adding additional photo...</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Mini thumbnail strip + upload another button */}
                      {evidenceForSelectedTask.length > 0 && (
                        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                          {/* Photo select dot indicators */}
                          <div className="flex gap-1.5 items-center">
                            {evidenceForSelectedTask.map((_, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setActivePhotoIdx(i)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  i === activePhotoIdx ? 'bg-primary w-4' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                                }`}
                              />
                            ))}
                          </div>

                          {/* Upload additional photo button */}
                          <button
                            type="button"
                            onClick={() => document.getElementById('evidence-upload-input-add')?.click()}
                            disabled={isUploadingActiveTask}
                            className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-bold px-2.5 py-1.5 rounded-md hover:bg-primary/5 transition-all disabled:opacity-50"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            <span>Add Photo</span>
                            <input
                              id="evidence-upload-input-add"
                              type="file"
                              accept="image/jpeg,image/png"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file && sessionId) {
                                  uploadEvidenceMutation.mutate({
                                    sessionId,
                                    taskId: selectedTaskId,
                                    file,
                                  });
                                  e.target.value = '';
                                }
                              }}
                            />
                          </button>
                        </div>
                      )}
                      </div>
                    </div>
                  </div>

                  {/* Right Pane: Task Resolution Worksheet */}
                  <div className="lg:col-span-5 flex flex-col">
                    <div className="flex-1 flex flex-col bg-card border rounded-xl p-4 shadow-sm space-y-4">
                      {/* Task info headers */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Task Details</span>
                          <span className="text-xs font-mono text-muted-foreground">ID: #{selectedTaskId}</span>
                        </div>
                        <h2 className="text-base font-bold text-foreground leading-tight">
                          {formatPlan?.campaignName ?? activeTask?.campaignName ?? `Task #${selectedTaskId}`}
                        </h2>
                        <div className="flex flex-wrap gap-1.5 pt-1.5">
                          {formatPlan?.taskType === 'REMOVAL' ? (
                            <Badge className="bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 flex items-center gap-1">
                              <Trash2 className="h-3 w-3" /> Removal
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 flex items-center gap-1">
                              <Package className="h-3 w-3" /> Install
                            </Badge>
                          )}
                          {formatPlan?.packName && (
                            <Badge variant="outline" className="border-border bg-muted/30">
                              Pack: {formatPlan.packName}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Discrepancy warning & quantities form */}
                      <div className="space-y-3 pt-2">
                        {operatorProgressForSelectedTask?.hasOperatorSubmission && (
                          <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3 flex items-start gap-2.5">
                            <Smartphone className="h-4.5 w-4.5 text-sky-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-bold text-sky-900">Operator synced this task</p>
                              <p className="text-[11px] text-sky-700 leading-snug mt-0.5">
                                {operatorProgressForSelectedTask.photoCount > 0
                                  ? `${operatorProgressForSelectedTask.photoCount} photo(s) on server. `
                                  : ''}
                                Quantities pre-filled from operator submission
                                {operatorProgressForSelectedTask.totalDelivered != null
                                  ? ` (${operatorProgressForSelectedTask.totalDelivered} total).`
                                  : '.'}
                                {' '}Use Evidence on By Store to approve operator work; complete here only if
                                admin override is still required.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Task resolved status banner */}
                        {activeTask?.status === 'admin_rejected' ? (
                          <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 flex items-start gap-2.5">
                            <XCircle className="h-4.5 w-4.5 text-rose-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-bold text-rose-800">Task rejected by admin</p>
                              <p className="text-[11px] text-rose-700 leading-snug mt-0.5">
                                This outstanding task was evaluated and rejected on behalf of the operator.
                              </p>
                            </div>
                          </div>
                        ) : activeTask?.status === 'admin_complete' || activeTask?.hasActiveOverride ? (
                          proofPublishStatus?.needsProofPublish ? (
                            <div className="rounded-lg border border-amber-300 bg-amber-50/70 p-3 space-y-3">
                              <div className="flex items-start gap-2.5">
                                <ShieldAlert className="h-4.5 w-4.5 text-amber-700 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs font-bold text-amber-900">
                                    Photos not yet in proof
                                  </p>
                                  <p className="text-[11px] text-amber-800 leading-snug mt-0.5">
                                    This task is already marked complete on the server, but{' '}
                                    {proofPublishStatus.unpublishedEvidenceCount} photo
                                    {proofPublishStatus.unpublishedEvidenceCount === 1 ? '' : 's'} from this session
                                    {proofPublishStatus.autoPublishOnUpload
                                      ? ' failed to auto-publish.'
                                      : ' still need publishing to gallery and proof of posting.'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                className="w-full sm:w-auto"
                                onClick={() => {
                                  if (!sessionId || !selectedTaskId) return;
                                  publishProofMutation.mutate({ sessionId, taskId: selectedTaskId });
                                }}
                                disabled={publishProofMutation.isPending}
                              >
                                {publishProofMutation.isPending ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Publishing…
                                  </>
                                ) : (
                                  'Publish to proof'
                                )}
                              </Button>
                            </div>
                          ) : (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 flex items-start gap-2.5">
                              <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-bold text-emerald-800">Work completed by admin</p>
                                <p className="text-[11px] text-emerald-700 leading-snug mt-0.5">
                                  Task complete and any on-behalf photos are in proof of posting.
                                </p>
                              </div>
                            </div>
                          )
                        ) : activeTask?.status === 'operator_complete' && proofPublishStatus?.needsProofPublish ? (
                          <div className="rounded-lg border border-amber-300 bg-amber-50/70 p-3 space-y-3">
                            <div className="flex items-start gap-2.5">
                              <ShieldAlert className="h-4.5 w-4.5 text-amber-700 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-bold text-amber-900">Attach on-behalf photos to proof</p>
                                <p className="text-[11px] text-amber-800 leading-snug mt-0.5">
                                  Operator already submitted this task. Publish your session photos to their proof
                                  submission, or use Evidence on By Store to review operator work.
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              className="w-full sm:w-auto"
                              onClick={() => {
                                if (!sessionId || !selectedTaskId) return;
                                publishProofMutation.mutate({ sessionId, taskId: selectedTaskId });
                              }}
                              disabled={publishProofMutation.isPending}
                            >
                              {publishProofMutation.isPending ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Publishing…
                                </>
                              ) : (
                                'Publish to proof'
                              )}
                            </Button>
                          </div>
                        ) : activeTask?.status === 'locked' ? (
                          <div className="rounded-lg border bg-muted/60 p-3 flex items-start gap-2.5">
                            <Clock className="h-4.5 w-4.5 text-zinc-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-bold text-zinc-800">Task is locked</p>
                              <p className="text-[11px] text-zinc-600 leading-snug mt-0.5">
                                This campaign has been locked or has dependencies that prevent modification.
                              </p>
                            </div>
                          </div>
                        ) : (
                          /* Pending - interactive form */
                          <>
                            {formatPlanLoading && (
                              <p className="text-xs text-muted-foreground animate-pulse">Loading format breakdown…</p>
                            )}

                            {!formatPlanLoading && formatPlan && formatPlan.formats.length > 0 && (
                              <div className="space-y-1">
                                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Delivered Quantities</Label>
                                <RecoveryFormatQuantitiesTable
                                  formats={formatPlan.formats}
                                  values={formatDelivered}
                                  discrepancyReasons={formatDiscrepancyReasons}
                                  onChange={(formatId, value) =>
                                    setFormatDelivered((prev) => ({ ...prev, [formatId]: value }))
                                  }
                                  onDiscrepancyReasonChange={(formatId, reason) =>
                                    setFormatDiscrepancyReasons((prev) => ({ ...prev, [formatId]: reason }))
                                  }
                                  deliveredLabel={deliveredLabel}
                                  showOperatorColumn
                                />
                              </div>
                            )}

                            {!formatPlanLoading && formatPlan && formatPlan.formats.length === 0 && (
                              <Alert className="bg-muted/30 border">
                                <AlertDescription className="text-xs">
                                  No format details exist for this campaign. Add resolution notes below.
                                </AlertDescription>
                              </Alert>
                            )}

                            {formatPlan?.warning && !formatPlan.assignmentFound && (
                              <Alert className="border-amber-300 bg-amber-50">
                                <ShieldAlert className="h-4 w-4 text-amber-700" />
                                <AlertTitle className="text-xs font-semibold text-amber-900">
                                  Never sent to operator
                                </AlertTitle>
                                <AlertDescription className="text-xs text-amber-800 space-y-1.5">
                                  <p>
                                    This task has no mobile brief. Send it via Task Management first,
                                    then complete on behalf if the operator still cannot finish.
                                  </p>
                                  {approveGate.requiresOverrideNote && (
                                    <p>
                                      For Emergency or External evidence sessions only: add approval
                                      notes below explaining why you are completing without a send.
                                    </p>
                                  )}
                                  {!approveGate.canApprove && approveGate.blockReason && (
                                    <p className="font-medium">{approveGate.blockReason}</p>
                                  )}
                                </AlertDescription>
                              </Alert>
                            )}

                            {formatPlan?.warning && formatPlan.assignmentFound && (
                              <p className="text-xs text-amber-700 italic bg-amber-50 p-2 rounded border border-amber-200">
                                {formatPlan.warning}
                              </p>
                            )}

                            <div className="space-y-1.5 pt-1">
                              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                                {approveGate.requiresOverrideNote
                                  ? 'Approval notes (required — completing without mobile brief)'
                                  : 'Approval notes (optional)'}
                              </Label>
                              <Textarea
                                value={resolveNotes}
                                onChange={(e) => setResolveNotes(e.target.value)}
                                placeholder="What was completed on site? Context for audit log..."
                                rows={2.5}
                                className="text-xs resize-none"
                              />
                            </div>

                            {showRejectForm && (
                              <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 space-y-2">
                                <Label className="text-xs font-bold text-red-900 uppercase">Rejection Reason (required)</Label>
                                <Textarea
                                  value={rejectionNotes}
                                  onChange={(e) => setRejectionNotes(e.target.value)}
                                  placeholder="Why is this work not accepted? Revisit directions..."
                                  rows={2.5}
                                  className="bg-white text-xs resize-none border-red-300"
                                />
                              </div>
                            )}

                            {/* Verification & Mutation triggers */}
                            <div className="flex flex-wrap gap-2.5 pt-2">
                              <Button
                                onClick={() => submitVerdict('APPROVE')}
                                className="font-semibold bg-emerald-600 hover:bg-emerald-500 text-white flex-1 min-w-[120px]"
                                disabled={
                                  resolveTaskMutation.isPending ||
                                  !formatDiscrepancyValidation.valid ||
                                  !approveGate.canApprove
                                }
                                title={
                                  !approveGate.canApprove && approveGate.blockReason
                                    ? approveGate.blockReason
                                    : undefined
                                }
                              >
                                {resolveTaskMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                                ) : (
                                  <CheckCircle2 className="h-4 w-4 mr-1.5" />
                                )}
                                Approve
                              </Button>

                              {!showRejectForm ? (
                                <Button
                                  variant="destructive"
                                  onClick={() => setShowRejectForm(true)}
                                  className="font-semibold flex-1 min-w-[120px]"
                                  disabled={
                                    resolveTaskMutation.isPending ||
                                    !formatDiscrepancyValidation.valid
                                  }
                                >
                                  <XCircle className="h-4 w-4 mr-1.5" />
                                  Reject…
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    variant="destructive"
                                    onClick={() => submitVerdict('REJECT')}
                                    className="font-semibold flex-1 min-w-[120px]"
                                    disabled={
                                      resolveTaskMutation.isPending || !rejectionNotes.trim()
                                    }
                                  >
                                    Confirm Reject
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      setShowRejectForm(false);
                                      setRejectionNotes('');
                                    }}
                                    className="flex-shrink-0"
                                    disabled={resolveTaskMutation.isPending}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Carousel Track Strip */}
            <div className="space-y-2.5 border-t pt-4">
              <div className="flex items-center justify-between gap-3 flex-wrap px-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Task List ({carouselTasks.length}
                    {showOnlyNeedsAdminAction ? ` of ${context.tasks.length}` : ''})
                  </span>
                  <Button
                    type="button"
                    variant={showOnlyNeedsAdminAction ? 'default' : 'outline'}
                    size="sm"
                    className={`h-7 text-[11px] font-semibold gap-1.5 ${
                      showOnlyNeedsAdminAction
                        ? 'bg-amber-600 hover:bg-amber-500 text-white'
                        : 'border-amber-200 text-amber-900 hover:bg-amber-50'
                    }`}
                    onClick={() => setShowOnlyNeedsAdminAction((value) => !value)}
                  >
                    <Filter className="h-3 w-3" />
                    {showOnlyNeedsAdminAction ? 'Showing needs action' : 'Needs action only'}
                    {!showOnlyNeedsAdminAction && needsAdminActionTasks.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="h-4 px-1 text-[10px] bg-amber-100 text-amber-900 border-amber-200"
                      >
                        {needsAdminActionTasks.length}
                      </Badge>
                    )}
                  </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[10px] font-semibold uppercase tracking-wide">
                  {(Object.keys(CAROUSEL_SOURCE_STYLES) as RecoveryTaskCompletionSource[]).map((source) => (
                    <span
                      key={source}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${CAROUSEL_SOURCE_STYLES[source].sourcePillClass}`}
                    >
                      {source === 'operator_synced' && <Smartphone className="h-2.5 w-2.5" />}
                      {source === 'admin_complete' && <ShieldCheck className="h-2.5 w-2.5" />}
                      {source === 'needs_admin_action' && <Clock className="h-2.5 w-2.5" />}
                      {source === 'admin_rejected' && <XCircle className="h-2.5 w-2.5" />}
                      {source === 'locked' && <Clock className="h-2.5 w-2.5" />}
                      {recoveryCompletionSourceLabel(source)}
                    </span>
                  ))}
                </div>
              </div>

              {carouselTasks.length === 0 ? (
                <div className="rounded-lg border border-dashed bg-muted/20 p-6 text-center">
                  <p className="text-sm font-medium text-foreground">No tasks need admin action</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    All remaining work was completed by the operator or admin. Turn off the filter to
                    review every task.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setShowOnlyNeedsAdminAction(false)}
                  >
                    Show all tasks
                  </Button>
                </div>
              ) : (
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-muted-foreground/20">
                {carouselTasks.map((t) => {
                  const isActive = selectedTaskId === t.campaignTaskId;
                  const firstTaskEvidence = sessionDetail?.evidence?.find(
                    (ev) => ev.campaignTaskId === t.campaignTaskId
                  );
                  const isRemoval = t.taskType === 'REMOVAL';
                  const completionSource = getRecoveryTaskCompletionSource(t);
                  const sourceStyles = CAROUSEL_SOURCE_STYLES[completionSource];
                  const thumbUrl =
                    completionSource === 'admin_complete'
                      ? (firstTaskEvidence?.secureUrl ?? t.operatorProgress?.thumbUrl ?? null)
                      : (t.operatorProgress?.thumbUrl ?? firstTaskEvidence?.secureUrl ?? null);

                  return (
                    <button
                      key={t.campaignTaskId}
                      type="button"
                      onClick={() => selectTaskForWork(t)}
                      title={`${t.campaignName} — ${recoveryCompletionSourceLabel(completionSource)}`}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 text-left flex-shrink-0 transition-all w-32 ${
                        sourceStyles.cardBorder
                      } ${sourceStyles.cardBg} ${
                        isActive
                          ? 'ring-2 ring-primary ring-offset-1 scale-105 shadow-md opacity-100'
                          : 'hover:scale-[1.02] opacity-80 hover:opacity-100'
                      }`}
                    >
                      {/* Thumbnail frame */}
                      <div
                        className={`w-full h-16 rounded-md overflow-hidden bg-slate-900 border-2 relative flex items-center justify-center ${sourceStyles.frameBorder}`}
                      >
                        {thumbUrl ? (
                          <img
                            src={thumbUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center text-slate-400">
                            {isRemoval ? (
                              <Trash2 className="h-5 w-5 text-orange-400" />
                            ) : (
                              <Package className="h-5 w-5 text-blue-400" />
                            )}
                          </div>
                        )}

                        {/* Completion source badge */}
                        <div className="absolute top-1 right-1">
                          {completionSource === 'admin_complete' ? (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-white border border-white shadow-sm">
                              <ShieldCheck className="h-3 w-3" />
                            </span>
                          ) : completionSource === 'operator_synced' ? (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sky-600 text-white border border-white shadow-sm">
                              <Smartphone className="h-3 w-3" />
                            </span>
                          ) : completionSource === 'admin_rejected' ? (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-white border border-white shadow-sm">
                              <XCircle className="h-3 w-3" />
                            </span>
                          ) : completionSource === 'locked' ? (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-500 text-white border border-white shadow-sm">
                              <Clock className="h-3 w-3" />
                            </span>
                          ) : (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white border border-white shadow-sm">
                              <Clock className="h-3 w-3 animate-pulse" />
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Truncated Name */}
                      <div className="w-full text-center space-y-0.5">
                        <p className="text-[11px] font-semibold text-foreground truncate w-full">
                          {t.campaignName}
                        </p>
                        <span
                          className={`inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${sourceStyles.sourcePillClass}`}
                        >
                          {completionSource === 'operator_synced' && (
                            <Smartphone className="h-2.5 w-2.5" />
                          )}
                          {completionSource === 'admin_complete' && (
                            <ShieldCheck className="h-2.5 w-2.5" />
                          )}
                          {recoveryCompletionSourceLabel(completionSource)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              )}
              <p className="text-[10px] text-muted-foreground px-1">
                Click a card to jump · Arrow keys navigate within the visible list
              </p>
            </div>

            {/* Session closure block */}
            <Card className="border border-border shadow-sm">
              <CardHeader className="py-4">
                <CardTitle className="text-sm font-bold flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>Close Session</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <Textarea
                  placeholder="Provide audit summary of actions completed on behalf..."
                  value={closeSummary}
                  onChange={(e) => setCloseSummary(e.target.value)}
                  rows={2}
                  className="text-xs resize-none"
                />
                <div className="flex items-center justify-between gap-4 flex-wrap pt-1">
                  <Button
                    variant="outline"
                    className="font-semibold border-amber-300 hover:bg-amber-50 hover:text-amber-900"
                    onClick={() => closeSessionMutation.mutate(sessionId)}
                    disabled={closeSessionMutation.isPending}
                  >
                    {closeSessionMutation.isPending ? 'Closing...' : 'Close on-behalf session'}
                  </Button>
                  {needsAdminActionTasks.length > 0 && (
                    <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1 rounded-md">
                      {needsAdminActionTasks.length} task(s) still need admin on-behalf action (use Approve).
                    </p>
                  )}
                  {!sessionIsOpen && sessionId && (
                    <p className="text-xs font-medium text-muted-foreground">
                      Session closed — publish any remaining photos before leaving this page.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </TrackerLayout>
  );
}
